import { useEffect, useState } from 'react';
import { ToggleLeft, Plus, Pencil, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../components/ui/ToastSystem';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader';

interface FeatureFlag {
  id: string;
  enabled: boolean;
  description: string | null;
  updated_at: string;
  updated_by: string | null;
}

export function AdminFeatureFlagsPage() {
  const { profile } = useAuth();
  const { showToast } = useToast();
  const [flags,   setFlags]   = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState<string | null>(null);

  // Create state
  const [creating,     setCreating]     = useState(false);
  const [newFlagId,    setNewFlagId]    = useState('');
  const [newFlagDesc,  setNewFlagDesc]  = useState('');

  // Edit state
  const [editingId,    setEditingId]    = useState<string | null>(null);
  const [editingDesc,  setEditingDesc]  = useState('');

  // Delete state
  const [deleting,     setDeleting]     = useState<string | null>(null);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    const { data } = await supabase
      .from('feature_flags')
      .select('*')
      .order('id');
    setFlags(data ?? []);
    setLoading(false);
  }

  async function toggle(flag: FeatureFlag) {
    if (!profile?.id) return;
    setSaving(flag.id);
    const newEnabled = !flag.enabled;
    const { error } = await supabase
      .from('feature_flags')
      .upsert({
        id:         flag.id,
        enabled:    newEnabled,
        description: flag.description,
        updated_at: new Date().toISOString(),
        updated_by: profile.id,
      }, { onConflict: 'id' });

    if (error) {
      showToast({ type: 'error', message: `Erro ao atualizar ${flag.id}` });
    } else {
      showToast({
        type: newEnabled ? 'success' : 'info',
        message: `${flag.id} ${newEnabled ? 'ativado' : 'desativado'}`,
      });
      setFlags(prev => prev.map(f => f.id === flag.id ? { ...f, enabled: newEnabled, updated_at: new Date().toISOString() } : f));
    }
    setSaving(null);
  }

  async function createFlag() {
    if (!newFlagId.trim() || !profile?.id) return;
    setSaving(newFlagId);
    const { error } = await supabase
      .from('feature_flags')
      .insert({
        id: newFlagId.trim(),
        enabled: false,
        description: newFlagDesc.trim() || null,
        updated_at: new Date().toISOString(),
        updated_by: profile.id,
      });
    if (error) {
      showToast({ type: 'error', message: `Erro ao criar flag: ${error.message}` });
    } else {
      showToast({ type: 'success', message: `Flag "${newFlagId}" criada (desativada)` });
      setCreating(false);
      setNewFlagId('');
      setNewFlagDesc('');
      load();
    }
    setSaving(null);
  }

  async function deleteFlag(flagId: string) {
    if (!confirm(`Excluir a flag "${flagId}"? Esta ação não pode ser desfeita.`)) return;
    setDeleting(flagId);
    const { error } = await supabase.from('feature_flags').delete().eq('id', flagId);
    if (error) {
      showToast({ type: 'error', message: `Erro ao excluir ${flagId}` });
    } else {
      showToast({ type: 'info', message: `Flag "${flagId}" excluída` });
      setFlags(prev => prev.filter(f => f.id !== flagId));
    }
    setDeleting(null);
  }

  async function saveEditDesc(flagId: string) {
    setSaving(flagId);
    const { error } = await supabase
      .from('feature_flags')
      .update({ description: editingDesc.trim() || null, updated_at: new Date().toISOString(), updated_by: profile?.id ?? null })
      .eq('id', flagId);
    if (error) {
      showToast({ type: 'error', message: 'Erro ao salvar descrição' });
    } else {
      setFlags(prev => prev.map(f => f.id === flagId ? { ...f, description: editingDesc.trim() || null } : f));
      setEditingId(null);
    }
    setSaving(null);
  }

  return (
    <div>
      <AdminPageHeader
        title="Feature Flags"
        objective="Ative ou desative funcionalidades do sistema sem precisar de atualização técnica. Útil para lançamentos graduais, testes A/B e resolução emergencial de problemas. As alterações têm efeito imediato para todos os usuários."
      />

      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => setCreating(v => !v)}
          style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}
        >
          <Plus size={13} /> Nova Flag
        </button>
      </div>

      {/* Create form */}
      {creating && (
        <div className="card" style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: 0, fontSize: 'var(--text-label)' }}>Nova Feature Flag</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '0.5rem' }}>
            <input
              className="form-input"
              placeholder="ID da flag (ex: ai_synastry)"
              value={newFlagId}
              onChange={e => setNewFlagId(e.target.value.replace(/[^a-z0-9_-]/g, '_'))}
              style={{ fontFamily: 'monospace' }}
            />
            <input
              className="form-input"
              placeholder="Descrição (o que esta flag controla)"
              value={newFlagDesc}
              onChange={e => setNewFlagDesc(e.target.value)}
            />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              className="btn btn-primary btn-sm"
              disabled={!newFlagId.trim() || saving !== null}
              onClick={createFlag}
            >
              Criar Flag
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => { setCreating(false); setNewFlagId(''); setNewFlagDesc(''); }}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Warning banner */}
      <div style={{ marginBottom: '1.25rem', padding: '0.75rem 1rem', backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 'var(--radius-md)' }}>
        <p style={{ fontSize: 'var(--text-caption)', color: '#fca5a5', margin: 0 }}>
          ⚠️ Desativar uma <span title="Feature Flag: interruptor de funcionalidade que permite ligar/desligar recursos do sistema sem alterações de código." style={{ borderBottom: '1px dashed #fca5a5', cursor: 'help' }}>feature flag</span> remove o acesso imediatamente para TODOS os usuários, incluindo pagantes. Use com cautela.
        </p>
      </div>

      {loading ? (
        <div className="card shimmer" style={{ height: '300px' }} />
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>
                  <span
                    title="Feature Flag: interruptor de funcionalidade que permite ligar/desligar recursos do sistema sem alterações de código."
                    style={{ borderBottom: '1px dashed var(--petrol)', cursor: 'help' }}
                  >
                    Flag
                  </span>
                </th>
                <th>Descrição</th>
                <th style={{ textAlign: 'center' }}>Status</th>
                <th>Última atualização</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {flags.map(flag => (
                <tr key={flag.id}>
                  <td style={{ fontFamily: 'monospace', fontSize: '13px', color: 'var(--petrol)', fontWeight: 600 }}>
                    {flag.id}
                  </td>
                  <td style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>
                    {editingId === flag.id ? (
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <input
                          className="form-input"
                          value={editingDesc}
                          onChange={e => setEditingDesc(e.target.value)}
                          style={{ flex: 1, fontSize: '13px', padding: '0.25rem 0.5rem' }}
                          autoFocus
                        />
                        <button className="btn btn-primary btn-sm" onClick={() => saveEditDesc(flag.id)} disabled={saving === flag.id}>✓</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => setEditingId(null)}>✕</button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span>{flag.description ?? <em style={{ color: 'var(--text-muted)' }}>sem descrição</em>}</span>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => { setEditingId(flag.id); setEditingDesc(flag.description ?? ''); }}
                          style={{ padding: '2px 6px', opacity: 0.6 }}
                          title="Editar descrição"
                        >
                          <Pencil size={11} />
                        </button>
                      </div>
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                      fontSize: '11px', padding: '2px 10px', borderRadius: '999px',
                      backgroundColor: flag.enabled ? 'rgba(74,222,128,0.12)' : 'rgba(239,68,68,0.1)',
                      color: flag.enabled ? '#4ade80' : '#f87171',
                      fontWeight: 600,
                    }}>
                      {flag.enabled ? '● Ativo' : '○ Inativo'}
                    </span>
                  </td>
                  <td style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    {new Date(flag.updated_at).toLocaleString('pt-BR')}
                  </td>
                  <td style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                    <button
                      className={flag.enabled ? 'btn btn-secondary btn-sm' : 'btn btn-primary btn-sm'}
                      onClick={() => toggle(flag)}
                      disabled={saving === flag.id}
                      style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', whiteSpace: 'nowrap' }}
                    >
                      <ToggleLeft size={13} />
                      {saving === flag.id ? '...' : flag.enabled ? 'Desativar' : 'Ativar'}
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => deleteFlag(flag.id)}
                      disabled={deleting === flag.id}
                      style={{ color: '#f87171', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                      title="Excluir flag"
                    >
                      <Trash2 size={13} />
                      {deleting === flag.id ? '...' : ''}
                    </button>
                  </td>
                </tr>
              ))}
              {flags.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic', padding: '2rem' }}>
                    Nenhuma flag encontrada
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
